import React, { useState, useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import ModalShell from './ModalShell';

// Set worker source - use unpkg which has better version availability
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const PDFPreviewModal = ({ isOpen, onClose, pdfUrl, title, token }) => {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [initialScale, setInitialScale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !pdfUrl) return;

    setLoading(true);
    setError(null);
    setCurrentPage(1);
    setInitialScale(null);

    const loadPdf = async () => {
      try {
        const isApiUrl = pdfUrl.includes('/api/');
        const isUploadsUrl = pdfUrl.includes('/uploads/');
        const isInternalUrl = isApiUrl || isUploadsUrl;

        let pdfData;
        if (isInternalUrl && isApiUrl) {
          const streamParam = pdfUrl.includes('/preview/') ? 'direct_url=true' : 'stream=true';
          const apiUrl = `${pdfUrl}${pdfUrl.includes('?') ? '&' : '?'}${streamParam}`;
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
            mode: 'cors',
            cache: 'no-store',
          });
          if (!response.ok) throw new Error('Failed to load PDF');
          const arrayBuffer = await response.arrayBuffer();
          pdfData = { data: arrayBuffer };
        } else if (isUploadsUrl) {
          const response = await fetch(pdfUrl, { mode: 'cors' });
          if (!response.ok) throw new Error('Failed to load PDF');
          const arrayBuffer = await response.arrayBuffer();
          pdfData = { data: arrayBuffer };
        } else {
          const response = await fetch(pdfUrl, { mode: 'cors', cache: 'no-store' });
          if (!response.ok) throw new Error('Failed to load PDF');
          const arrayBuffer = await response.arrayBuffer();
          pdfData = { data: arrayBuffer };
        }

        const doc = await pdfjsLib.getDocument(pdfData).promise;
        setPdfDoc(doc);
        setNumPages(doc.numPages);

        const firstPage = await doc.getPage(1);
        const viewport = firstPage.getViewport({ scale: 1 });

        const availableHeight = window.innerHeight * 0.9 - 100;
        const availableWidth = window.innerWidth * 0.9 - 60;
        const scaleToFitHeight = availableHeight / viewport.height;
        const scaleToFitWidth = availableWidth / viewport.width;
        const optimalScale = Math.min(scaleToFitHeight, scaleToFitWidth);

        setInitialScale(optimalScale);
        setScale(optimalScale);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError('Failed to load PDF document');
        setLoading(false);
      }
    };

    loadPdf();

    return () => {
      if (pdfDoc) {
        pdfDoc.destroy();
        setPdfDoc(null);
      }
    };
  }, [isOpen, pdfUrl, token]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    let cancelled = false;
    let renderTask = null;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');

        const viewport = page.getViewport({ scale });
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        renderTask = page.render({
          canvasContext: context,
          viewport: viewport
        });
        await renderTask.promise;
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('Error rendering page:', err);
        }
      }
    };

    renderPage();

    return () => {
      cancelled = true;
      if (renderTask) renderTask.cancel();
    };
  }, [pdfDoc, currentPage, scale]);

  // Handle keyboard navigation (arrows for pages)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && currentPage > 1) {
        setCurrentPage(p => p - 1);
      } else if (e.key === 'ArrowRight' && currentPage < numPages) {
        setCurrentPage(p => p + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentPage, numPages]);

  const handleDownload = async () => {
    const isApiUrl = pdfUrl.includes('/api/');

    if (token && isApiUrl) {
      try {
        const streamParam = pdfUrl.includes('/preview/') ? 'direct_url=true' : 'stream=true';
        const apiUrl = `${pdfUrl}${pdfUrl.includes('?') ? '&' : '?'}${streamParam}`;
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Failed to download PDF');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = title || 'document.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Download error:', err);
        alert('Failed to download file');
      }
    } else {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = title || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const headerControls = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage <= 1}
          className="pdf-nav-btn"
        >
          <i className="bi bi-chevron-left"></i> Prev
        </button>
        <span style={{ fontSize: '0.875rem', color: '#666', minWidth: '80px', textAlign: 'center' }}>
          Page {currentPage} of {numPages}
        </span>
        <button
          onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
          disabled={currentPage >= numPages}
          className="pdf-nav-btn"
        >
          Next <i className="bi bi-chevron-right"></i>
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button onClick={() => setScale(s => Math.max(0.3, s - 0.1))} className="pdf-nav-btn" title="Zoom out">
          <i className="bi bi-zoom-out"></i> Out
        </button>
        <button onClick={() => initialScale && setScale(initialScale)} className="pdf-nav-btn" title="Fit to page">
          <i className="bi bi-arrows-fullscreen"></i> Fit
        </button>
        <button onClick={() => setScale(s => Math.min(3, s + 0.1))} className="pdf-nav-btn" title="Zoom in">
          <i className="bi bi-zoom-in"></i> In
        </button>
      </div>
      <button onClick={handleDownload} className="pdf-download-btn" title="Download PDF">
        <i className="bi bi-download"></i> Download
      </button>

      <style>{`
        .pdf-nav-btn {
          background: none;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 6px 10px;
          cursor: pointer;
          color: #333;
          transition: all 0.2s;
        }
        .pdf-nav-btn:hover:not(:disabled) {
          background: #e9ecef;
          border-color: #072040;
        }
        .pdf-nav-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .pdf-download-btn {
          background: #072040;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 6px 12px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .pdf-download-btn:hover {
          background: #004080;
        }
      `}</style>
    </>
  );

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title || 'PDF Preview'}
      headerControls={headerControls}
      bodyStyle={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        background: '#525659',
        paddingTop: '20px',
        position: 'relative',
      }}
    >
      {loading && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: 'white', zIndex: 1 }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: '12px' }}>Loading PDF...</p>
        </div>
      )}
      {error && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: 'white', zIndex: 1 }}>
          <i className="bi bi-exclamation-triangle" style={{ fontSize: '3rem', color: '#ffc107' }}></i>
          <p style={{ marginTop: '12px' }}>{error}</p>
        </div>
      )}
      <div style={{ padding: '20px', visibility: loading || error ? 'hidden' : 'visible' }}>
        <canvas ref={canvasRef} style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)', background: 'white' }} />
      </div>
    </ModalShell>
  );
};

export default PDFPreviewModal;
